import React from "react"
import Editor from '@kehila/react-page-editor'
import {Form, notification} from "antd"
import {t} from "@lingui/core/macro"

import {cellPlugins} from "../WebEditor/plugins/cellPlugins"
import FormPlugin from "../WebEditor/formPlugins"
import logger from "../../services/logger"

// ─────────────────────────────────────────────────────────────────────
//  AccountEditorRender — renderer de formulaire pour l'Espace de compte.
//
//  Clone ÉPURÉ de WebEditor/form.js (FormEditorRender) : réutilise le moteur
//  react-page + les form plugins (identity, champs custom, PhoneInput, i18n, RTL,
//  validation), SANS la machinerie du tunnel forms_submit (pas de POST, pas de
//  localStorage MEM_*, pas de produits/paiement).
//
//  La soumission remonte les données BRUTES via `onData(data)`. Réutilisable pour
//  l'inscription ET l'édition (initialValues).
//
//  ⚠️ PAS de spinner / état `loading` : les plugins sont construits dans le
//  constructeur (synchrone). Le rendu de react-page est client-only (gardé par
//  `mounted` posé en componentDidMount) pour éviter tout souci SSR/Next.js.
// ─────────────────────────────────────────────────────────────────────

export default class AccountEditorRender extends React.Component {

    constructor(props) {
        super(props)
        this.state = {mounted: false}
        this.formPlugin = null
        this.buildPlugins()
    }

    buildPlugins() {
        try {
            let content = null
            if (this.props.form?.content) {
                content = JSON.parse(this.props.form.content)
            }
            // live=true → mode rendu. formData = le form (identity, type…).
            this.formPlugin = new FormPlugin(this, this.props.form, content, null, true)
            this.formPlugin.setOrg(this.props.org || {})
            this.plugins = this.formPlugin.getPlugins(cellPlugins)
        } catch (e) {
            logger.error({ err: e }, "[AccountEditorRender] buildPlugins error")
            this.plugins = []
        }
    }

    componentDidMount() {
        this.setState({mounted: true})
    }

    // Déclenche la soumission depuis l'extérieur (ex. bouton « Enregistrer » du
    // panneau Profile, porté hors du <Form> interne). Délègue au Form Antd via la
    // ref posée par le FormPlugin.
    submit() {
        const ref = this.formPlugin?.formRef
        if (ref && typeof ref.submit === "function") {
            ref.submit()
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.form && this.props.form &&
            JSON.stringify(prevProps.form) !== JSON.stringify(this.props.form)) {
            this.buildPlugins()
            this.forceUpdate()
        }
    }

    onChange(v) {
        this.formPlugin.setTree(v)
        this.formPlugin.checkFormPlugins()
    }

    handleFinish(data) {
        try {
            if (this.formPlugin?.isSubmitErrored && this.formPlugin.isSubmitErrored(data)) {
                return false
            }
        } catch (_) { /* form synthétique sans products → ignore */ }
        if (this.props.onData) {
            this.props.onData(data)
        }
    }

    render() {
        const {form, lang, initialValues} = this.props

        // react-page ne se rend que côté client.
        if (!this.state.mounted) {
            return null
        }

        let content = null
        if (form?.content) {
            content = JSON.parse(form.content)
        }

        return (
            <Form
                ref={(ref) => this.formPlugin.setFormRef(ref)}
                layout="vertical"
                initialValues={initialValues || {}}
                onFinishFailed={() => {
                    notification.error({
                        message: t`Champs obligatoires`,
                        description: t`Veuillez renseigner tous les champs obligatoires.`,
                        placement: "bottomRight",
                    })
                }}
                onFinish={(data) => this.handleFinish(data)}
                onValuesChange={(_, allValues) => {
                    this.formPlugin.setFormValues(allValues)
                    this.formPlugin.checkConditions()
                }}
            >
                <Editor
                    cellPlugins={this.plugins || []}
                    value={this.formPlugin.hookValueEditor(content)}
                    onChange={(v) => this.onChange(v)}
                    cellSpacing={{x: 12, y: 0}}
                    readOnly
                    lang={lang}
                />
            </Form>
        )
    }
}
